import {
	booleanAttribute,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewChildren,
	ChangeDetectionStrategy,
} from "@angular/core"
import {
	Contactway,
	insertPatientFields,
	Patient,
} from "@app/definitions/types"
import { GenAutocompleteComponent } from "@app/gen-inputs/gen-autocomplete/gen-autocomplete.component"
import { Subject, debounceTime } from "rxjs"
import { Modalable } from "@app/Modalable"
import { COMMON_IMPORTS } from "../common-imports"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { AutocompleteWithTagsComponent } from "@app/gen-inputs/autocomplete-with-tags/autocomplete-with-tags.component"
import {
	BtnExtComponent,
	CancelBtnComponent,
	ContactwayAddComponent,
} from "@app/utils/utils.component"
import { TaExpandsComponent } from "@app/utils/ta-expands/ta-expands.component"
import { DatePickerComponent } from "@app/utils/date-picker/date-picker.component"
import { WysiwygComponent } from "@app/utils/wysiwyg/wysiwyg.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { GenTableAddItemComponent } from "@app/gen-table-add-item/gen-table-add-item.component"
import { ConfirmComponent } from "@app/confirm/confirm.component"

@Component({
	selector: "app-insert-patient-form",
	imports: [
		COMMON_IMPORTS,
		LangValPipe,
		GenInputComponent,
		GenButtonGroupComponent,
		GenAutocompleteComponent,
		AutocompleteWithTagsComponent,
		ContactwayAddComponent,
		TaExpandsComponent,
		DatePickerComponent,
		WysiwygComponent,
		CancelBtnComponent,
		BtnExtComponent,
		ModalWrapComponent,
		GenTableAddItemComponent,
		ConfirmComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./insert-patient-form.component.html",
})
export class InsertPatientFormComponent extends Modalable {
	modalProps: any = null
	@Input({ transform: booleanAttribute }) hasUpdate: boolean = false

	insert_patient_template: any = {}
	patient: Patient = new Patient()
	errors: any = {} //will hold errors for the fields in the form
	isFormSubmittable: boolean = false //can submit (controls disabled in the submit button)
	isFormSubmittableWithInvite: boolean = false //can submit (controls disabled in the submit button)
	validationFields: any = {
		//field validations, control displaying field errors and ability to submit the form
		name: { not_empty: null },
	}
	@Input() inputObj: any = null
	hasPerms: boolean = false
	tagIds: number[] = []
	isSaving: boolean = false

	@ViewChild("tagFuzzyInput") tagFuzzyInput: GenAutocompleteComponent
	@ViewChild("payorFuzzyInput") payorFuzzyInput: GenAutocompleteComponent
	@ViewChildren(GenAutocompleteComponent) genAutocompletes

	// tabindexes: any =insertPatientFields.reduce((acc,item,index)=>{
	//   const obj={...acc};
	//   obj[item]=index+2;
	//   return obj;
	// },{name:1});

	tabindexes: any = {
		//used to assign recalculated tab indexes to fields (will be updated to fit all within this modal, without conflict to other modals/components on screen. seen modal-gen for calulation.)
		//implemented in this component's HTML.
		// recipient: 1,
		// title: 2,
		// html_content: 3,
		// submit: 4
	}

	payors: any[] = []
	patienttypes: any[] = []
	patientfields: any[] = []
	@Input() meeting_request: any = null
	mrMessage: any = null

	referers: any[] = []
	contactways: Contactway[] = []
	excludeIds: number[] = []

	ngOnInit() {
		// this.insert_patient_template=get from cc
		this.payors = this.store.getSiteDataTable("payors") //get from store (siteData in sessionStorage)
		this.patienttypes = this.store.getSiteDataTable("patienttypes")
		this.patientfields = this.store.getPreppedPatientFields()
		this.patientfields
			.filter((it) => it.type !== "text")
			.forEach((it) => {
				it.optsArr = (it.opts || "").split(",").map((opt) => ({ value: opt }))
			})

		this.hasPerms =
			!this.permsService.owner_has_users ||
			this.permsService?.perms?.administrative ||
			this.permsService?.perms?.add_new_patient

		this.insert_patient_template = this.store.cc()?.insert_patient_template
		if (!this.insert_patient_template) {
			this.insert_patient_template = insertPatientFields.reduce(
				(acc, item) => ({ ...acc, [item]: true }),
				{}
			)
		}

		this.addReferer()

		let didOpenContactway = false
		if (this.meeting_request) {
			this.patient.referrer_user_id = this.meeting_request.user_id
			this.patient.name = this.meeting_request.name || ""
			this.patient.remarks = this.meeting_request.remarks || ""
			if (this.meeting_request.email || this.meeting_request.phone) {
				didOpenContactway = true
				if (this.meeting_request.email) {
					const obj = this.addContactway(true)
					obj.contactway = this.meeting_request.email
					this.validateContactwayTaken(obj)
				}
				if (this.meeting_request.phone) {
					const obj = this.addContactway(true)
					obj.contactway = this.meeting_request.phone
					this.validateContactwayTaken(obj)
				}
			}
			this.getMrMessage()
		}

		if (this.inputObj) {
			const fields = Object.keys(this.inputObj).filter((k) => k !== "phone")
			fields.forEach((k) => (this.patient[k] = this.inputObj[k]))
			if (this.inputObj.phone) {
				didOpenContactway = true
				const obj = this.addContactway(true)
				obj.contactway = this.inputObj.phone
				this.validateContactwayTaken(obj)
			}
			if (this.inputObj.email) {
				didOpenContactway = true
				const obj = this.addContactway(true)
				obj.contactway = this.inputObj.email
				this.validateContactwayTaken(obj)
			}
		}
		if (!didOpenContactway) {
			this.addContactway(true)
		}
		this.excludeIds = this.store.getNonClinicalSubUserIds()
	}
	async getMrMessage() {
		const { mrMessage } = await this.store.getMeetingRequestData(
			this.meeting_request
		)
		this.mrMessage = mrMessage
	}
	choosePayor(obj: any = null) {
		this.patient.payor_id = obj?.id || 0
	}

	validateEntireForm() {
		this.isFormSubmittable = this.validator.validateEntireForm(
			this.patient,
			this.errors,
			this.validationFields
		)

		this.contactways.forEach((c) => {
			c.errors = {}
			const isSubmittable = this.validator.validateEntireForm(c, c.errors, {
				contactway: { not_empty: true },
			})
			if (!isSubmittable) {
				this.isFormSubmittable = false
			}
			if (c.inValidation) {
				this.isFormSubmittable = false
			}
		})
		this.isFormSubmittableWithInvite = this.isFormSubmittable
		if (!this.contactways.length) {
			this.isFormSubmittableWithInvite = false
		}
	}

	updateSubUser(objs: any[]) {
		//TODO
		;(this.patient.assoc as any) = objs.map((it) => it.id)
		this.validateEntireForm()
	}

	chooseReferer(row: any, obj: any = null) {
		row.referer_id = obj?.id || 0
	}
	getReferersTopId() {
		if (!this.referers.length) {
			return 0
		}
		return this.referers[this.referers.length - 1].id
	}
	addReferer() {
		const nextId = this.getReferersTopId() + 1
		this.referers.push({ id: nextId, referer_id: 0 })
	}
	removeReferer(id: number) {
		this.referers = this.referers.filter((it) => it.id != id)
	}

	handleAddRefererClose(obj: any = null) {
		const { referer } = this.modalProps
		this.modalProps = null
		if (!obj) {
			return
		}
		const el = this.genAutocompletes.find((inp) =>
			inp.className.includes("referer_id_" + referer.id)
		)
		referer.referer_id = obj?.id || 0
		if (el) {
			el.searchStr = obj.name
		}
	}
	openAddPayorModal() {
		this.modalProps = {
			name: "add-payor",
			fuzzyName: this.payorFuzzyInput.returnSearchStrForAdding(),
		}
	}
	handleAddPayorClose(obj: any = null) {
		if (obj) {
			//expects either NULL if nothing happened or object of new inserted row!
			this.patient.payor_id = obj.id
			this.payorFuzzyInput.searchStr = obj.name
		}
		this.validateEntireForm()
	}

	getContactwayTopId() {
		if (!this.contactways.length) {
			return 0
		}
		return this.contactways[this.contactways.length - 1].id
	}
	async validateContactwayTaken(contactway: Contactway) {
		if (!contactway.inValidation || !contactway.contactway) {
			return
		}
		contactway.warning_status = "validating"
		contactway.resultObjs = []
		this.validateEntireForm()
		const patientIds = await this.apiService.post("get_contactway_patients", {
			value: contactway.contactway,
		})

		contactway.inValidation = false
		contactway.warning_status = patientIds?.length
			? this.lang.getVal("patient")
			: null
		contactway.resultObjs = patientIds
			.map((id) => this.store.searchSiteDataItem(id, ["patients"]))
			.filter(Boolean)
		this.validateEntireForm()
	}
	handleIdClicked(id: number) {
		this.onClose.emit()
		this.router.navigateByUrl(`/patients/${id}`)
	}
	keyupContactway(contactway: Contactway) {
		contactway.subj?.next(contactway.contactway)
		contactway.inValidation = true
		contactway.warning_status = null
		contactway.resultObjs = []

		this.validateEntireForm()
	}
	addContactway(avoidValidation: boolean = false) {
		const nextId = this.getContactwayTopId() + 1
		const obj = new Contactway()
		obj.subj = new Subject()
		obj.subj.pipe(debounceTime(1500)).subscribe(() => {
			this.validateContactwayTaken(obj)
		})
		obj.warning_status = null
		obj.resultObjs = []
		obj.id = nextId

		this.contactways = [...this.contactways, obj]
		if (!avoidValidation) {
			this.validateEntireForm()
		}
		return obj
	}
	removeContactway(id: number) {
		this.contactways = this.contactways.filter((it) => it.id != id)
		this.validateEntireForm()
	}

	async save(routeOnEnd: boolean = false) {
		const item = this.store
			.getSiteDataTable("patients")
			.find((it) => it.name == this.patient.name)
		if (item) {
			this.modalProps = {
				name: "confirm-duplicate",
				routeOnEnd,
				objectName: item.name,
			}
			return
		}
		this.modalProps = { routeOnEnd }
		this.handleDuplicateApprove(true)
	}
	async handleDuplicateApprove(approved: boolean) {
		const { routeOnEnd } = this.modalProps
		this.modalProps = null
		if (!approved) {
			return
		}

		this.isSaving = true

		const contactways = [...this.contactways]
		contactways.forEach((c) => {
			delete c.subj
		})

		const sendObj = {
			...this.patient,
			referers: this.referers.map((it) => it.referer_id),
			contactways,
			tagIds: this.tagIds,
		}
		const res: any = await this.apiService.save_gen_item("patients", sendObj)
		if (res) {
			res.typeClassName = "patients"
		}
		if (res?.id) {
			this.store.addGenItemRow("patients", res)
		}
		if (res?.tags?.length) {
			res.tags.forEach((tag) => this.store.addGenItemRow("object_to_tags", tag))
		}
		this.isSaving = false
		this.onClose.emit(
			res ? { ...res, doClose: routeOnEnd } : { doClose: routeOnEnd }
		)
		this.store.refreshTableSubject.next("patients")
		this.store.refreshTableSubject.next("inquirers")
		if (routeOnEnd && res?.id) {
			this.router.navigateByUrl("/patients/" + res?.id)
		}
	}
	chooseTags(tags: any[]) {
		this.tagIds = tags.map((it) => it.id)
	}

	updateExcludedIds(tagObjs: any[]) {
		this.excludeIds = this.store.getExcludedIdsByTagObjs(tagObjs)
	}
}
